<template>
  <section id="screenings-filters" class="form-row evollu-form">
    <div class="col-12 col-sm-4">
      <va-input
        id="screening-query"
        :label="`${$t('agenda.screenings.query')}:`"
        v-model="searchQuery"
        :placeholder="$t('agenda.screenings.query_placeholder')"
      />
    </div>

    <div class="col-12 col-sm-4">
      <range-date-picker
        id="selected-time-date-picker"
        :label="`${$t('agenda.screenings.date_range')}:`"
        v-model="filters.dateRange"
        placeholder="---"
        :clearable="false"
        >
      </range-date-picker>
    </div>

    <div class="col-12 col-sm-4">
      <label class="form-label">{{ $t('agenda.screenings.entities') }}:</label>
      <multiselect
        v-model="selectedEntityOptions"
        :options="entityOptions"
        track-by="uid"
        :customLabel="formatedEntityLabel"
        select-label=""
        deselect-label="×"
        placeholder="---"
        :allow-empty="true"
        :multiple="true"
        :close-on-select="false"
        >
        <template slot="clear">
          <span class="multiselect__clear" v-if="selectedEntityOptions.length > 0" @mousedown.prevent.stop="selectedEntityOptions = []"></span>
        </template>
      </multiselect>
    </div>

    <div class="col-12 col-sm-4" v-if="isFromEvollu">
      <label class="form-label">{{ $t('agenda.screenings.audiology_service') }}:</label>
      <multiselect
        v-model="filters.audProviders"
        :options="availableAudProviders"
        track-by="id"
        label="name"
        select-label=""
        deselect-label="×"
        placeholder="---"
        :allow-empty="true"
        :multiple="true"
        :close-on-select="false"
        >
        <template slot="clear">
          <span class="multiselect__clear" v-if="filters.audProviders.length > 0" @mousedown.prevent.stop="filters.audProviders = []"></span>
        </template>
      </multiselect>
    </div>

    <div class="col-12 col-sm-4" v-if="isFromEvollu">
      <label class="form-label">{{ $t('agenda.screenings.screening_providers') }}:</label>
      <multiselect
        v-model="filters.screeningProviders"
        :options="availableScreeningProviders"
        track-by="id"
        label="name"
        select-label=""
        deselect-label="×"
        placeholder="---"
        :allow-empty="true"
        :multiple="true"
        :close-on-select="false"
        >
        <template slot="clear">
          <span class="multiselect__clear" v-if="filters.screeningProviders.length > 0" @mousedown.prevent.stop="filters.screeningProviders = []"></span>
        </template>
      </multiselect>
    </div>

  </section>
</template>

<script>
import { debounce } from 'lodash'
import { mapGetters } from 'vuex'
import MultiselectLabelFormatter from '@/mixins/MultiselectLabelFormatter'
import ClustersAndEntitiesMixin from '@/mixins/ClustersAndEntitiesMixin'
import IsFromEvolluMixin from '@/mixins/IsFromEvolluMixin'
import Multiselect from 'vue-multiselect'
import RangeDatePicker from '@/components/common/RangeDatePicker'
import LocalStorageHelper from '@/local-storage/LocalStorageHelper'
import { FILTERS_SCREENINGS } from '@/local-storage/LocalStorageKeys'

export default {
  components: {
    RangeDatePicker,
    Multiselect
  },
  mixins: [
    MultiselectLabelFormatter,
    ClustersAndEntitiesMixin,
    IsFromEvolluMixin
  ],
  computed: {
    ...mapGetters([
      'clusters',
      'availableScreeningProviders',
      'availableAudProviders'
    ]),
    ebookingService: function () {
      return this.$store.state.services.find(service => service.url === this.$store.state.ebooking_server)
    },
    readableEntities: function () {
      let userPermissions = this.ebookingService.user_permissions.filter(permission => permission.info_type === 200 && permission.access_type >= 1)
      if (userPermissions.length === 0) {
        return []
      } else {
        if (userPermissions.some(permission => permission.entity_id === null)) {
          return [...this.ebookingService.entities]
        } else {
          return this.ebookingService.entities.filter(entity => userPermissions.some(permission => permission.entity_id === entity.id))
        }
      }
    },
    entityOptions: function () {
      return this.createClustersAndEntitiesList(this.clusters, this.readableEntities)
    },
  },
  data: function () {
    return {
      hasLoaded: false,
      searchQuery: '',
      selectedEntityOptions: [],
      filters: {
        query: '',
        dateRange: {
          start: this.moment().format('YYYY-MM-DD'),
          end: this.moment().add(1, 'M').format('YYYY-MM-DD')
        },
        entities: [],
        audProviders: [],
        screeningProviders: [],
        selectedEntityIds: [],
        selectedEntityUids: []
      }
    }
  },
  created: function () {
    this.loadLocalStorage()
  },
  methods: {
    loadLocalStorage: function () {
      const state = LocalStorageHelper.get(FILTERS_SCREENINGS)

      if (state !== null) {
        if (state.query) {
          this.filters.query = state.query
          this.searchQuery = this.filters.query
        }

        if (state.dateRange && state.dateRange.start && state.dateRange.end) {
          this.filters.dateRange = state.dateRange
        }

        if (Array.isArray(state.selectedEntityUids) && state.selectedEntityUids.length) {
          this.selectedEntityOptions = this.reloadSelectedClustersAndEntities(this.entityOptions, state.selectedEntityUids)
        }

        if (Array.isArray(state.selectedAudProviderIds) && state.selectedAudProviderIds.length) {
          this.filters.audProviders = this.ebookingService.entities.filter(entity => state.selectedAudProviderIds.includes(entity.id))
        }

        if (Array.isArray(state.selectedScreeningProviderIds) && state.selectedScreeningProviderIds.length) {
          this.filters.screeningProviders = this.ebookingService.entities.filter(entity => state.selectedScreeningProviderIds.includes(entity.id))
        }
      }

      this.$emit('filters-updated', { filters: this.filters })
      this.$nextTick(() => {
        this.hasLoaded = true
        this.$emit('loaded')
      })
    },
    saveLocalStorage: function () {
      LocalStorageHelper.set(FILTERS_SCREENINGS, {
        query: this.filters.query,
        dateRange: this.filters.dateRange,
        selectedAudProviderIds: this.filters.audProviders.map(e => e.id),
        selectedScreeningProviderIds: this.filters.screeningProviders.map(e => e.id),
        selectedEntityUids: this.filters.selectedEntityUids
      })
    }
  },
  watch: {
    searchQuery: debounce(function (value) {
      this.filters.query = value
    }, 500),
    selectedEntityOptions: function (options) {
      const selectedEntityIds = this.getSelectedOptionsIdsAndUids(options)
      this.filters.selectedEntityIds = selectedEntityIds.ids
      this.filters.selectedEntityUids = selectedEntityIds.uids
      this.filters.entities = this.readableEntities.filter(entity => this.filters.selectedEntityIds.includes(entity.id))
    },
    filters: {
      handler: function (value) {
        if (this.hasLoaded) {
          this.$emit('filters-updated', { filters: this.filters })
          this.saveLocalStorage()
        }
      },
      deep: true
    }
  }
}
</script>

<style lang="scss" scoped>

#screenings-filters {
  padding-bottom: 2rem;
}
</style>
